import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Copy, Users, CheckCircle, Clock, Award, Target } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralProgram = () => {
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0 });
  const [milestones, setMilestones] = useState({
    rewards_earned: [],
    total_rewards: 0,
    next_milestone: 5,
    referrals_to_next_milestone: 5
  });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [codeRes, referralsRes] = await Promise.all([
        axios.get(`${API}/provider/referral-code`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/provider/referrals`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setReferralCode(codeRes.data.referral_code);
      setReferrals(referralsRes.data.referrals || []);
      setStats({
        total: referralsRes.data.total || 0,
        completed: referralsRes.data.completed || 0
      });

      // Set milestone data if available
      if (referralsRes.data.milestones) {
        setMilestones(referralsRes.data.milestones);
      }
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast.success('Code kopiert!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="h-32 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8">
      <div className="flex items-center gap-3 mb-6">
        <Gift className="w-6 h-6 text-[#2B2142]" />
        <h2 className="text-2xl font-bold text-gray-900">Empfehlungsprogramm</h2>
      </div>

      {/* Referral Code Section */}
      <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-r from-[#FFF0D1]/30 to-[#F4F2F2] border-2 border-[#2B2142]/20 rounded-xl sm:rounded-2xl mx-0 sm:mx-0">
        <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">Dein persönlicher Empfehlungscode</h3>
        <p className="text-xs sm:text-sm text-gray-600 mb-4 leading-relaxed">
          Teile diesen Code mit anderen Dienstleistern. Wenn sie sich mit deinem Code für den <strong>Basic-Monatsplan</strong> anmelden, erhalten sie <strong>1 zusätzlichen Freimonat</strong> (insgesamt 60 Tage kostenlos)!
        </p>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 px-4 sm:px-6 py-3 sm:py-4 bg-white border-2 border-[#2B2142]/30 rounded-xl flex items-center justify-center">
            <span className="text-lg sm:text-2xl font-mono font-bold text-[#2B2142] break-all">{referralCode}</span>
          </div>
          <button
            onClick={handleCopyCode}
            className={`w-full sm:w-auto px-6 py-3 sm:py-4 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 min-h-[44px] ${
              copied
                ? 'bg-green-600 text-white'
                : 'bg-[#2B2142] hover:bg-[#1E1730] text-white'
            }`}
          >
            {copied ? (
              <>
                <CheckCircle className="w-5 h-5" />
                Kopiert!
              </>
            ) : (
              <>
                <Copy className="w-5 h-5" />
                Kopieren
              </>
            )}
          </button>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="p-6 bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-2xl">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6 text-[#2B2142]" />
            <h3 className="font-bold text-gray-900">Geworbene Dienstleister</h3>
          </div>
          <p className="text-4xl font-bold text-[#2B2142]">{stats.total}</p>
        </div>

        <div className="p-6 bg-green-50 border border-green-200 rounded-2xl">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <h3 className="font-bold text-gray-900">Erfolgreich aktiviert</h3>
          </div>
          <p className="text-4xl font-bold text-green-600">{stats.completed}</p>
        </div>
      </div>

      {/* Milestone Progress Section */}
      <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl">
        <div className="flex items-center gap-3 mb-4">
          <Award className="w-6 h-6 text-green-600" />
          <h3 className="font-bold text-gray-900">Meilenstein-Prämien</h3>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Bei jeder 5. erfolgreichen Empfehlung erhältst <strong>DU</strong> einen Monat gratis!
        </p>

        {/* Progress to next milestone */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Fortschritt zum nächsten Meilenstein</span>
            <span className="font-bold text-green-700">{stats.completed} / {milestones.next_milestone}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((stats.completed / milestones.next_milestone) * 100, 100)}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {milestones.referrals_to_next_milestone > 0 ? (
              <>Noch <strong className="text-green-700">{milestones.referrals_to_next_milestone}</strong> Empfehlung{milestones.referrals_to_next_milestone !== 1 ? 'en' : ''} bis zum nächsten Freimonat!</>
            ) : (
              <span className="text-green-700 font-semibold">Meilenstein erreicht!</span>
            )}
          </p>
        </div>

        {/* Rewards earned */}
        {milestones.total_rewards > 0 && (
          <div className="p-4 bg-white rounded-xl border border-green-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Gift className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {milestones.total_rewards} Freimonat{milestones.total_rewards !== 1 ? 'e' : ''}
                </p>
                <p className="text-sm text-gray-600">bereits verdient und gutgeschrieben</p>
              </div>
            </div>

            {/* Individual rewards list */}
            {milestones.rewards_earned && milestones.rewards_earned.length > 0 && (
              <div className="space-y-2 border-t border-green-100 pt-3">
                {milestones.rewards_earned.map((reward, index) => (
                  <div key={index} className="flex items-center justify-between py-2 px-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">
                        {reward.milestone_number}. Empfehlung
                      </span>
                      {reward.applied_at && (
                        <span className="text-xs text-gray-500">
                          · {new Date(reward.applied_at).toLocaleDateString('de-DE')}
                        </span>
                      )}
                    </div>
                    <span className="font-bold text-green-600 text-sm">
                      -{(reward.credit_amount_cents / 100).toFixed(2).replace('.', ',')} €
                    </span>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-3">
              💡 Guthaben wird automatisch auf deine nächste Abo-Rechnung angerechnet.
              <a href="/provider/dashboard?tab=subscription" className="text-green-600 hover:underline ml-1">
                Zum Abo →
              </a>
            </p>
          </div>
        )}

        {/* Next milestones preview */}
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
          <Target className="w-4 h-4" />
          <span>Nächste Meilensteine: {milestones.next_milestone}, {milestones.next_milestone + 5}, {milestones.next_milestone + 10}...</span>
        </div>
      </div>

      {/* Referrals List */}
      <div>
        <h3 className="font-bold text-gray-900 mb-4">Geworbene Dienstleister</h3>
        
        {referrals.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">Noch keine Empfehlungen</p>
            <p className="text-sm text-gray-500">
              Teile deinen Code und erhalte Anerkennung für deine Empfehlungen!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {referrals.map((referral) => (
              <div
                key={referral.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${
                    referral.status === 'completed'
                      ? 'bg-green-100'
                      : 'bg-yellow-100'
                  }`}>
                    {referral.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {referral.referred_provider_name || 'Neuer Dienstleister'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      Beigetreten am {formatDate(referral.referred_provider_joined || referral.created_at)}
                    </p>
                  </div>
                </div>
                <div>
                  {referral.status === 'completed' ? (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                      Aktiviert
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                      Ausstehend
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-8 p-6 bg-purple-50 border border-purple-200 rounded-2xl">
        <h4 className="font-bold text-purple-900 mb-3">Wie funktioniert es?</h4>
        <ul className="space-y-2 text-sm text-purple-800">
          <li className="flex items-start gap-2">
            <span className="font-bold">1.</span>
            <span>Teile deinen persönlichen Empfehlungscode mit anderen Event-Dienstleistern.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">2.</span>
            <span>Wenn sie sich registrieren und deinen Code beim ersten Abo-Abschluss eingeben, erhalten sie <strong>1 zusätzlichen Monat kostenlos</strong> (30 Tage Standard-Testphase + 30 Tage mit deinem Code = 60 Tage total).</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-green-700">3.</span>
            <span><strong className="text-green-700">NEU:</strong> Bei jeder 5. erfolgreichen Empfehlung (5, 10, 15, 20...) erhältst <strong className="text-green-700">DU</strong> 1 Monat gratis als Gutschrift auf deine nächste Rechnung!</span>
          </li>
        </ul>
        <p className="mt-4 text-xs text-purple-700">
          <strong>Hinweis:</strong> Deine Gutschriften werden automatisch auf deine nächste Rechnung angerechnet.
        </p>
      </div>
    </div>
  );
};

export default ReferralProgram;
